import React from 'react';
import { User, Calendar, Phone, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import { SectionHeader } from './ui/SectionHeader';
import { MedicalFormData } from '../hooks/useFormState';
interface PersonalDetailsProps {
  data: MedicalFormData;
  onChange: (field: keyof MedicalFormData, value: any) => void;
  errors: Partial<Record<keyof MedicalFormData, string>>;
}
export function PersonalDetails({
  data,
  onChange,
  errors
}: PersonalDetailsProps) {
  return <Card className="h-full" data-id="element-143">
      <CardHeader data-id="element-144">
        <SectionHeader title="Personal Details" subtitle="Patient information and contact details" icon={<User className="w-5 h-5" data-id="element-146" />} data-id="element-145" />
      </CardHeader>
      <CardContent className="space-y-6" data-id="element-147">
        {/* Are You Field */}
        <div data-id="element-148">
          <Label data-id="element-149">Are You (क्या आप)</Label>
          <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.areYou} onChange={e => onChange('areYou', e.target.value)} data-id="element-150">
            <option value="" data-id="element-151">Select</option>
            <option value="Patient" data-id="element-152">Patient (मरीज़)</option>
            <option value="Acupuncture Student" data-id="element-153">
              Acupuncture Student (छात्र)
            </option>
            <option value="Acupuncturist" data-id="element-154">Acupuncturist</option>
            <option value="Therapist" data-id="element-155">Therapist</option>
          </select>
        </div>

        {/* Branch Location Dropdown */}
        <div data-id="element-156">
          <Label data-id="element-157">Branch Location</Label>
          <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.location} onChange={e => onChange('location', e.target.value)} data-id="element-158">
            <option value="" data-id="element-159">Select Branch Location</option>
            <option value="Plot No 41-42, Keshav Nagar, Opposite Trasha Dry Cleaners Lucknow U.P. Pin-226020 Mob: +91 8303699990 (Dr. Amit Kapoor)" data-id="element-160">
              Plot No 41-42, Keshav Nagar, Opposite Trasha Dry Cleaners Lucknow
              U.P. Pin-226020 Mob: +91 8303699990 (Dr. Amit Kapoor)
            </option>
            <option value="Adeshwar kripa #6, Ground Floor, Chowdappa Road, Shanti Nagar, Bangalore 560027 Mob: +91 9632132911(Sheeetal M Bohra)" data-id="element-161">
              Adeshwar kripa #6, Ground Floor, Chowdappa Road, Shanti Nagar,
              Bangalore 560027 Mob: +91 9632132911(Sheeetal M Bohra)
            </option>
            <option value="A305, Shriram Southern Crest, JP Nagar 6th Phase, Near Metro, Bangalore - 560078. Mob: +91 8050158700 (Sunitanjani Miriyala)" data-id="element-162">
              A305, Shriram Southern Crest, JP Nagar 6th Phase, Near Metro,
              Bangalore - 560078. Mob: +91 8050158700 (Sunitanjani Miriyala)
            </option>
            <option value="179, 1st floor, 6th cross, Ramesh nagar, Marathalli post, Bangalore -560037 Mobile: 9845856582(Mithun M Vernekar)" data-id="element-163">
              179, 1st floor, 6th cross, Ramesh nagar, Marathalli post,
              Bangalore -560037 Mobile: 9845856582(Mithun M Vernekar)
            </option>
            <option value="B-319 Garden City, Ankleshwar, Dist. Bharuch. Gujarat 393002 Mobile: +919909209557, +91 9998293923(Anisha Yogesh Potdar)" data-id="element-164">
              B-319 Garden City, Ankleshwar, Dist. Bharuch. Gujarat 393002
              Mobile: +919909209557, +91 9998293923(Anisha Yogesh Potdar)
            </option>
            <option value="501 Swarnvilla 1, Opposite Swersangini Near DGVCL Office VIP Road Althan Surat (Gujarat) Mobille: +91 9376630760(Sanjeeta Nahata)" data-id="element-165">
              501 Swarnvilla 1, Opposite Swersangini Near DGVCL Office VIP Road
              Althan Surat (Gujarat) Mobille: +91 9376630760(Sanjeeta Nahata)
            </option>
          </select>
        </div>

        {/* Basic Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-166">
          <div data-id="element-167">
            <Label className="flex items-center gap-2" data-id="element-168">
              <Calendar className="w-4 h-4 text-amber-600" data-id="element-169" />
              Admission Date
            </Label>
            <Input type="date" value={data.admissionDate} onChange={e => onChange('admissionDate', e.target.value)} data-id="element-170" />
          </div>

          <div data-id="element-171">
            <Label data-id="element-172">Reference</Label>
            <Input value={data.reference} onChange={e => onChange('reference', e.target.value)} placeholder="Reference source" data-id="element-173" />
          </div>

          <div data-id="element-174">
            <Label data-id="element-175">Regd No.</Label>
            <Input value={data.regdNo} onChange={e => onChange('regdNo', e.target.value)} placeholder="Registration number" data-id="element-176" />
          </div>
        </div>

        {/* Patient Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-177">
          <div data-id="element-178">
            <Label className="required" data-id="element-179">Patient Name (मरीज़ का नाम) *</Label>
            <Input value={data.name} onChange={e => onChange('name', e.target.value)} placeholder="Full name" className={errors.name ? 'border-red-500' : ''} data-id="element-180" />
            {errors.name && <p className="text-red-600 text-sm mt-1" data-id="element-181">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-182">
            <div data-id="element-183">
              <Label data-id="element-184">Gender (लिंग)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.gender} onChange={e => onChange('gender', e.target.value)} data-id="element-185">
                <option value="" data-id="element-186">Select</option>
                <option value="M" data-id="element-187">Male (पुरुष)</option>
                <option value="F" data-id="element-188">Female (महिला)</option>
                <option value="Other" data-id="element-189">Other (अन्य)</option>
              </select>
            </div>
            <div data-id="element-190">
              <Label data-id="element-191">Age (आयु)</Label>
              <Input type="number" value={data.age} onChange={e => onChange('age', e.target.value)} placeholder="Years" data-id="element-192" />
            </div>
          </div>
        </div>

        {/* Guardian and Occupation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-193">
          <div data-id="element-194">
            <Label data-id="element-195">Guardian Name (अभिभावक का नाम)</Label>
            <Input value={data.guardianName} onChange={e => onChange('guardianName', e.target.value)} placeholder="Father/Mother/Spouse name" data-id="element-196" />
          </div>

          <div data-id="element-197">
            <Label data-id="element-198">Occupation (व्यवसाय)</Label>
            <Input value={data.occupation} onChange={e => onChange('occupation', e.target.value)} placeholder="Current occupation" data-id="element-199" />
          </div>
        </div>

        {/* Address Section - Updated Layout */}
        <div className="space-y-4 pt-4 border-t border-slate-100" data-id="element-200">
          <div className="flex items-center gap-2 mb-2" data-id="element-201">
            <MapPin className="w-4 h-4 text-amber-600" data-id="element-202" />
            <Label className="text-base font-semibold" data-id="element-203">Address Details</Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-204">
            {/* Left side - Address */}
            <div data-id="element-205">
              <Label data-id="element-206">Address (पता)</Label>
              <Textarea value={data.address} onChange={e => onChange('address', e.target.value)} placeholder="Complete address" rows={4} data-id="element-207" />
            </div>

            {/* Right side - City and Pincode stacked */}
            <div className="space-y-4" data-id="element-208">
              <div data-id="element-209">
                <Label data-id="element-210">City (शहर)</Label>
                <Input value={data.city} onChange={e => onChange('city', e.target.value)} placeholder="City name" data-id="element-211" />
              </div>
              <div data-id="element-212">
                <Label data-id="element-213">Pincode (पिन कोड)</Label>
                <Input value={data.pincode} onChange={e => onChange('pincode', e.target.value)} placeholder="6-digit pincode" data-id="element-214" />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="pt-4 border-t border-slate-100" data-id="element-215">
          <div className="flex items-center gap-2 mb-3" data-id="element-216">
            <Phone className="w-4 h-4 text-amber-600" data-id="element-217" />
            <Label className="text-base font-semibold" data-id="element-218">
              Contact Information
            </Label>
          </div>

          <div data-id="element-219">
            <Label className="required" data-id="element-220">Mobile Number (मोबाइल नंबर) *</Label>
            <Input type="tel" value={data.mobile} onChange={e => onChange('mobile', e.target.value)} placeholder="10-digit mobile number" className={errors.mobile ? 'border-red-500' : ''} data-id="element-221" />
            {errors.mobile && <p className="text-red-600 text-sm mt-1" data-id="element-222">{errors.mobile}</p>}
          </div>
        </div>

        {/* Chief Complaints */}
        <div className="pt-4 border-t border-slate-100" data-id="element-223">
          <Label className="text-base font-semibold" data-id="element-224">
            Chief Complaints (मुख्य शिकायतें)
          </Label>
          <Textarea value={data.chiefComplaints} onChange={e => onChange('chiefComplaints', e.target.value)} placeholder="Describe main health concerns and symptoms..." rows={4} className="mt-2" data-id="element-225" />
        </div>
      </CardContent>
    </Card>;
}